/**
 * Samples table component for shipping details
 * Displays samples with ability to add, remove, and edit label types
 */

'use client';

import React, { FC } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { Delete, Search } from '@mui/icons-material';
import { FormLabel } from '@/components/common/FormLabel';
import { ShippingDto } from '@/api/ApiClient';
import { formatDate } from '@/lib/utils/dateUtils';
import { formatMaterials, formatSubstrate } from '@/lib/utils/sampleUtils';

interface ShippingSamplesTableProps {
  shipping: ShippingDto;
  isLocalTransfer: boolean;
  isPending: boolean;
  isSubmitted: boolean;
  isReadOnly: boolean;
  availableLabelTypes: string[];
  sampleLabelToAdd: string;
  onSampleLabelChange: (value: string) => void;
  onAddSample: () => void;
  onRemoveSample: (sampleId: number) => void;
  onLabelTypeChange: (sampleId: number, labelType: string) => void;
  onShowSampleDialog: () => void;
  /** Whether samples are required to submit */
  requiredToSubmit?: boolean;
  /** Error message if samples validation failed */
  errorMessage?: string;
}

export const ShippingSamplesTable: FC<ShippingSamplesTableProps> = ({
  shipping,
  isLocalTransfer,
  isPending,
  isSubmitted,
  isReadOnly,
  availableLabelTypes,
  sampleLabelToAdd,
  onSampleLabelChange,
  onAddSample,
  onRemoveSample,
  onLabelTypeChange,
  onShowSampleDialog,
  requiredToSubmit = false,
  errorMessage,
}) => {
  const hasError = !!errorMessage;
  const hasSamples = shipping.samples && shipping.samples.length > 0;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      className="shipping-paper"
      sx={{
        borderColor: hasError ? 'error.main' : undefined,
        borderWidth: hasError ? 2 : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6" component="span">
          Samples
        </Typography>
        {requiredToSubmit && !isReadOnly ? (
          <FormLabel requiredToSubmit isEditMode={true}>
            {''}
          </FormLabel>
        ) : null}
      </Box>
      {errorMessage ? (
        <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
          {errorMessage}
        </Typography>
      ) : null}
      <TableContainer className="shipping-table-container">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Sample</TableCell>
              <TableCell>Material</TableCell>
              <TableCell>Substrate</TableCell>
              <TableCell>Growth Date</TableCell>
              {!isLocalTransfer ? <TableCell>Label Type</TableCell> : null}
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipping.samples && shipping.samples.length > 0 ? (
              shipping.samples.map((sampleLine, index) => {
                const sample = sampleLine.sample;
                return (
                  <React.Fragment key={sampleLine.sampleId}>
                    <TableRow>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{sample?.sampleLabel ?? 'N/A'}</TableCell>
                      <TableCell>{formatMaterials(sample?.materials)}</TableCell>
                      <TableCell>{formatSubstrate(sample)}</TableCell>
                      <TableCell>{sample?.creationDate ? formatDate(sample.creationDate) : 'N/A'}</TableCell>
                      {!isLocalTransfer ? (
                        <TableCell>
                          {isPending && !isReadOnly ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={sampleLine.labelType || ''}
                                onChange={(e) => onLabelTypeChange(sampleLine.sampleId, e.target.value)}
                                displayEmpty
                              >
                                <MenuItem value="" disabled>
                                  Select a label type
                                </MenuItem>
                                {availableLabelTypes.map((type) => (
                                  <MenuItem key={type} value={type}>
                                    {type}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            sampleLine.labelType || 'N/A'
                          )}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        {!isSubmitted && !isReadOnly ? (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveSample(sampleLine.sampleId)}
                            aria-label="Remove Sample"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {sample?.description ? (
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell colSpan={isLocalTransfer ? 5 : 6}>
                          <Box
                            dangerouslySetInnerHTML={{
                              __html: sample.description,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={isLocalTransfer ? 6 : 7} align="center">
                  No samples added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isSubmitted && !isReadOnly ? (
        <Box
          className="shipping-add-sample-controls"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, mt: 1 }}
        >
          <TextField
            size="small"
            placeholder="Add Sample to shipping"
            value={sampleLabelToAdd}
            onChange={(e) => onSampleLabelChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onAddSample();
              }
            }}
            className="shipping-add-sample-input"
            error={!!(hasError && !hasSamples)}
            helperText={hasError && !hasSamples ? errorMessage : undefined}
            sx={{ flex: '1 1 240px', minWidth: 0 }}
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={onShowSampleDialog}
                  aria-label="Look up samples to add"
                  title="Look up samples"
                  edge="end"
                  sx={{ mr: 0.25 }}
                >
                  <Search fontSize="small" />
                </IconButton>
              ),
            }}
          />
          <Button variant="contained" size="small" onClick={onAddSample} disabled={!sampleLabelToAdd.trim()}>
            Add Sample
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
};
